import React from 'react';
import { Zap, Activity, Gauge } from 'lucide-react';

const PerformanceWizard = ({ data = {}, setData }) => {
  
  const handleToggle = (option) => {
    setData({
      ...data,
      perfOpts: { ...data.perfOpts, [option]: !data.perfOpts[option] }
    });
  };

  return (
    <div className="animate-in fade-in duration-300 text-left w-full space-y-6">
      
      {/* NOTA */}
      <div className="bg-rose-900/10 border-l-4 border-rose-600 p-5 rounded-r-3xl flex gap-4 items-start">
        <div className="bg-rose-600 p-2 rounded-xl text-white shrink-0">
          <Gauge size={18} />
        </div>
        <div>
          <p className="text-[11px] text-rose-400 font-black uppercase tracking-widest mb-1">
            Performance Engineering Note
          </p>
          <p className="text-[11px] text-slate-400 leading-relaxed">
            Esta herramienta analiza la eficiencia algorítmica de tu código. Se enfoca en reducir 
            el tiempo de CPU mediante <strong>Field Symbols</strong>, optimización de lectura de 
            tablas internas (Binary Search / Hashed Keys) y eliminación de <strong>Nested Loops</strong>. 
            Pegá el bloque de código lento para recibir la versión optimizada.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* COLUMNA 1 */}
        <div className="bg-[#0F172A] p-6 rounded-[28px] text-white border-b-4 border-rose-500 space-y-4">
          <label className="text-[10px] font-black text-rose-400 uppercase flex items-center gap-2 border-b border-slate-800 pb-2">
            <Activity size={14}/> Runtime Bottleneck Analysis
          </label>
          
          <div>
            <label className="text-[9px] font-bold text-slate-400 uppercase block mb-1">
              Área Crítica Detectada (ST12/SAT)
            </label>
            <select className="w-full p-2.5 bg-slate-800 border-none rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-rose-500"
              value={data.perfBottleneck || 'ABAP_LOGIC'}
              onChange={e => setData({...data, perfBottleneck: e.target.value})}>
              <option value="ABAP_LOGIC">Lógica ABAP (CPU Intenso)</option>
              <option value="DB_ACCESS">Acceso a DB (Lecturas largas)</option>
              <option value="MEMORY">Consumo de Memoria (Internal Tables)</option>
            </select>
          </div>

          <div>
            <label className="text-[9px] font-bold text-slate-400 uppercase block mb-1">
              Volumen de Datos Estimado
            </label>
            <select className="w-full p-2.5 bg-slate-800 border-none rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-rose-500"
              value={data.perfVolume || 'MEDIUM'}
              onChange={e => setData({...data, perfVolume: e.target.value})}>
              <option value="LOW">Bajo (1 - 1,000 registros)</option>
              <option value="MEDIUM">Medio (1,000 - 50,000 registros)</option>
              <option value="HIGH">Alto (50,000+ registros)</option>
            </select>
          </div>

          <div className="p-4 bg-rose-500/10 rounded-2xl border border-rose-500/20">
            <p className="text-[10px] text-rose-300 leading-relaxed">
              Para volúmenes altos la IA priorizará <strong>Hashed Tables</strong> y <strong>Field Symbols</strong> para máximo rendimiento.
            </p>
          </div>
        </div>

        {/* COLUMNA 2 */}
        <div className="bg-[#0F172A] p-6 rounded-[28px] text-white border-b-4 border-rose-500 space-y-4">
          <label className="text-[10px] font-black text-rose-400 uppercase flex items-center gap-2 border-b border-slate-800 pb-2">
            <Zap size={14}/> Optimization Techniques
          </label>

          <div className="space-y-3">
            {[
              { key: "fieldSymbols",  label: "Field Symbols",           desc: "Sin work areas — acceso directo a memoria" },
              { key: "binarySearch",  label: "Binary Search / Keys",    desc: "Índices secundarios y BINARY SEARCH" },
              { key: "noNested",      label: "Avoid Nested Loops",      desc: "Eliminar LOOP AT dentro de LOOP AT" },
              { key: "hashedTables",  label: "Hashed Tables",           desc: "READ TABLE con clave única O(1)" },
              { key: "parallelCursor", label: "Parallel Cursor",        desc: "Lectura paralela de tablas ordenadas" },
              { key: "bufferTables",  label: "Buffered Tables",         desc: "Usar tablas con buffer SAP activo" },
            ].map((opt) => (
              <label key={opt.key}
                className={`flex items-center gap-3 cursor-pointer p-3 rounded-xl border transition-all ${
                  data.perfOpts?.[opt.key]
                    ? 'bg-rose-600/20 border-rose-500/50'
                    : 'bg-slate-800 border-slate-700 hover:border-slate-600'
                }`}>
                <div className="flex-1">
                  <p className={`text-[10px] font-black uppercase ${data.perfOpts?.[opt.key] ? 'text-rose-400' : 'text-slate-400'}`}>
                    {opt.label}
                  </p>
                  <p className="text-[9px] text-slate-500">{opt.desc}</p>
                </div>
                <input type="checkbox" className="w-4 h-4 rounded shrink-0"
                  checked={data.perfOpts?.[opt.key] || false}
                  onChange={() => handleToggle(opt.key)} />
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceWizard;